import { format } from 'date-fns';

import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useTranslate } from 'src/locales';
import { CallOutcome, NextCallPreset, NextCallPreview, CallListEntryStatus } from 'src/services/call-lists/call-lists.types';

import { formatDateTime } from '../utils';
import { NextCallChoice } from '../hooks/use-current-call';

// ----------------------------------------------------------------------

const PRESETS: NextCallPreset[] = [
  NextCallPreset.THREE_BUSINESS_DAYS,
  NextCallPreset.ONE_WEEK,
  NextCallPreset.TWO_WEEKS,
  NextCallPreset.ONE_MONTH,
  NextCallPreset.TWO_MONTHS,
  NextCallPreset.NEVER,
];

type Props = {
  outcome: CallOutcome | null;
  /** What the list's defaults would do for this outcome, from the server. */
  preview: NextCallPreview | null;
  value: NextCallChoice | null;
  onChange: (value: NextCallChoice | null) => void;
  disabled?: boolean;
};

/**
 * Next call: the list's default for the chosen outcome, with quick picks and a date to override it.
 * Wrong number and do not call end the entry, so there is nothing to pick.
 */
export default function NextCallPicker({ outcome, preview, value, onChange, disabled }: Props) {
  const { t } = useTranslate();

  if (outcome === CallOutcome.WRONG_NUMBER || outcome === CallOutcome.DO_NOT_CALL) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('call_lists.next_call.drops')}
      </Typography>
    );
  }

  const defaultText = (() => {
    if (!outcome || !preview) return t('call_lists.next_call.pick_outcome');
    if (preview.requires_choice) return t('call_lists.next_call.choose');
    if (preview.status === CallListEntryStatus.UNREACHABLE) return t('call_lists.next_call.unreachable');
    if (preview.next_call_at) return t('call_lists.next_call.default', { when: formatDateTime(preview.next_call_at) });
    return '';
  })();

  const pickedDate = value?.preset === NextCallPreset.DATE ? value.date ?? '' : '';

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color={preview?.requires_choice && !value ? 'warning.main' : 'text.secondary'}>
        {value ? t(`call_lists.next_call.picked.${value.preset}`, { date: pickedDate }) : defaultText}
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {PRESETS.map((preset) => (
          <Chip
            key={preset}
            size="small"
            disabled={disabled || !outcome}
            label={t(`call_lists.next_call.presets.${preset}`)}
            color={value?.preset === preset ? 'primary' : 'default'}
            variant={value?.preset === preset ? 'filled' : 'outlined'}
            onClick={() => onChange(value?.preset === preset ? null : { preset })}
          />
        ))}
        <TextField
          type="date"
          size="small"
          disabled={disabled || !outcome}
          value={pickedDate}
          label={t('call_lists.next_call.date')}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: format(new Date(), 'yyyy-MM-dd') }}
          onChange={(event) => onChange(event.target.value ? { preset: NextCallPreset.DATE, date: event.target.value } : null)}
          sx={{ width: 170 }}
        />
      </Stack>
    </Stack>
  );
}
